import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
	getShareableClientDetails,
	generateShareDemoToken,
	notifySlackShareDemo,
	getShareableCredits,
} from '@/api/user';
import { Room, RoomEvent, ConnectionState } from 'livekit-client';
import { motion, AnimatePresence } from 'framer-motion';
import { Alert, Button, notification } from 'antd';
import { Phone, PhoneCall, Mic, RefreshCw, ExternalLink } from 'lucide-react';
import ReCaptchaVerification from '@/components/ReCaptchaVerification';
import { RoomAudioRenderer, RoomContext, useVoiceAssistant } from '@livekit/components-react';
import '@livekit/components-styles';
import { StarFilled, TrophyFilled, HeartFilled } from '@ant-design/icons';
import { Avatar } from 'antd';
import logo from '@/assets/images/new_logo.png';
import { ClientDetails, TokenResponse } from '@/interfaces/ShareDemo';
import { AgentStateIndicator } from '@/components/ShareDemo/AgentState';
import posthog from 'posthog-js';
import { useFavicon } from '@/routes/hooks/useFavicon';
import { SHARE_DEMO_CALENDLY_URL } from 'src/constants';
import { useClarity } from 'src/Hooks/useClarity';

const SERVER_URL = import.meta.env.VITE_LIVEKIT_SERVER_URL;

const MAX_CALL_DURATION = 5 * 60;

const userCancelledRef = { current: false };

const ErrorBlock = ({ title, description }: { title: string; description?: string }) => (
	<Alert type="error" message={title} description={description} showIcon />
);

const Spinner = () => (
	<div className="flex items-center justify-center min-h-screen">
		<motion.div
			className="w-16 h-16 border-4 border-blue-300 border-t-blue-600 rounded-full animate-spin"
			initial={{ scale: 0.8, opacity: 0 }}
			animate={{ scale: 1, opacity: 1 }}
			transition={{ duration: 0.4 }}
			role="status"
		/>
	</div>
);

const VoiceAssistantInterface = ({
	businessName,
	isConnecting,
	isCheckingCredits,
	onStartCall,
	onEndCall,
	roomConnectionState,
	hasPermission,
	username,
	clientDetails,
	callInterfaceDetails,
	source,
}: {
	businessName: string;
	isConnecting: boolean;
	isCheckingCredits: boolean;
	onStartCall: () => void;
	onEndCall: () => void;
	roomConnectionState: ConnectionState;
	hasPermission?: boolean | null;
	username?: string;
	clientDetails?: any;
	callInterfaceDetails: {
		title: string;
		description: string;
		buttonText: string;
		subText: string;
	};
	source?: string | null;
}) => {
	const { state: agentState } = useVoiceAssistant();
	const clarity = useClarity();
	const [callDuration, setCallDuration] = useState(0);
	const callTimerInterval = useRef<NodeJS.Timeout | null>(null);
	const hasNotifiedAgentRef = useRef(false);

	useEffect(() => {
		if (agentState === 'listening' || agentState === 'speaking' || agentState === 'thinking') {
			if (!callTimerInterval.current) {
				setCallDuration(0);
				callTimerInterval.current = setInterval(() => {
					setCallDuration((prev) => {
						const newDuration = prev + 1;
						if (newDuration >= MAX_CALL_DURATION) {
							onEndCall();
						}
						return newDuration;
					});
				}, 1000);
			}
		} else {
			if (callTimerInterval.current) {
				clearInterval(callTimerInterval.current);
				callTimerInterval.current = null;
			}
			if (agentState === 'disconnected') {
				setCallDuration(0);
			}
		}
		return () => {
			if (agentState === 'disconnected' && callTimerInterval.current) {
				clearInterval(callTimerInterval.current);
				callTimerInterval.current = null;
			}
		};
	}, [agentState, onEndCall]);

	useEffect(() => {
		if (
			agentState === 'speaking' &&
			!hasNotifiedAgentRef.current &&
			username &&
			clientDetails?.data?.agentId
		) {
			try {
				notifySlackShareDemo(username, source);
			} catch {
				// Don't disrupt the flow
			}
			hasNotifiedAgentRef.current = true;
		}
	}, [agentState, username, clientDetails]);

	const isFullyConnected =
		agentState === 'listening' || agentState === 'speaking' || agentState === 'thinking';
	const isConnectedButLoading =
		roomConnectionState === ConnectionState.Connected &&
		(agentState === 'disconnected' || agentState === 'connecting');
	const formatCallTime = (seconds: number) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs.toString().padStart(2, '0')}`;
	};
	const getConnectionStatusText = () => {
		if (isCheckingCredits) {
			return 'Checking Compatibility...';
		}
		if (isConnecting) {
			return 'Connecting...';
		}
		if (isConnectedButLoading) {
			return 'Initializing call...';
		}
		if (isFullyConnected) {
			return 'Connected';
		}
		return 'Disconnected';
	};
	const getConnectionStatusColor = () => {
		if (isCheckingCredits || isConnecting || isConnectedButLoading) {
			return 'text-yellow-600';
		}
		if (isFullyConnected) {
			return 'text-green-600';
		}
		return 'text-gray-600';
	};

	const startCallEvent = () => {
		if (window.gtag) {
			window.gtag('event', 'start_demo_call', {
				event_category: 'ShareDemo',
				event_label: source === 'email' ? 'Email Source' : 'Share Source',
			});
		}
		if (posthog) {
			posthog.capture('start_demo_call', {
				location: 'share_demo',
				source,
				username,
				...clientDetails?.data,
			});
		}
		clarity('event', 'start_demo_call');
		onStartCall();
	};
	return (
		<>
			<RoomAudioRenderer />
			<div className="flex-1 flex flex-col items-center justify-center min-h-[32rem]">
				<div className="text-center max-w-md relative">
					{(isCheckingCredits || isConnecting || isConnectedButLoading) && (
						<button
							aria-label="Cancel"
							onClick={() => {
								userCancelledRef.current = true;
								onEndCall();
							}}
							className="absolute top-2 right-2 text-gray-400 hover:text-red-500 text-xl font-bold bg-white bg-opacity-80 rounded-full w-8 h-8 flex items-center justify-center shadow"
							style={{ zIndex: 10 }}
						>
							&times;
						</button>
					)}
					{isFullyConnected ? (
						<>
							<motion.div
								key="call-active"
								className="w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6"
								animate={{ scale: [1, 1.1, 1] }}
								transition={{ duration: 2, repeat: Infinity }}
							>
								<PhoneCall className="w-12 h-12 text-white" />
							</motion.div>

							<h2 className="text-2xl font-bold text-gray-900 mb-2">Connected to {businessName}</h2>

							<div className="text-sm text-green-600 mb-4">Call Active</div>

							<div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
								<div className="text-center">
									<p className="text-lg font-semibold text-blue-800">
										{clientDetails?.data?.phoneNumber ?? 'Browser Call'}
									</p>
								</div>
							</div>

							<AgentStateIndicator agentState={agentState} />

							<div className="text-3xl font-mono text-blue-600 mb-6">
								{formatCallTime(callDuration)}
							</div>

							<Button
								type="primary"
								size="large"
								icon={<Phone className="w-5 h-5" />}
								onClick={onEndCall}
								className="h-12 px-8 text-lg font-semibold bg-gradient-to-r from-red-500 to-red-600 border-0 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
							>
								End Call
							</Button>
						</>
					) : isCheckingCredits || isConnecting || isConnectedButLoading ? (
						<>
							<motion.div
								key="call-connecting"
								className="w-24 h-24 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6"
								animate={{ rotate: 360 }}
								transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
							>
								<RefreshCw className="w-12 h-12 text-white" />
							</motion.div>

							<h2 className="text-2xl font-bold text-gray-900 mb-4">{getConnectionStatusText()}</h2>

							<p className="text-gray-600 mb-6">
								{isCheckingCredits
									? 'Verifying Compatibility...'
									: isConnectedButLoading
										? 'Setting up your call with the AI receptionist...'
										: 'Establishing connection to the AI receptionist...'}
							</p>

							<div className={`text-sm font-medium ${getConnectionStatusColor()}`}>
								Status: {getConnectionStatusText()}
							</div>
						</>
					) : (
						<>
							<motion.div
								key="call-idle"
								className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6"
								animate={{ scale: [1, 1.1, 1] }}
								transition={{ duration: 2, repeat: Infinity }}
							>
								<Mic className="w-12 h-12 text-white" />
							</motion.div>

							<h2 className="text-2xl font-bold text-gray-900 mb-4">
								{source === 'email' ? (
									<>
										<span className="text-yellow-500 font-semibold">{businessName}</span>
										<br /> Here's your free AI receptionist
									</>
								) : (
									<>
										Test the <span className="text-yellow-500 font-semibold">{businessName}</span>{' '}
										AI Receptionist
									</>
								)}
							</h2>

							<p className="text-gray-600 mb-8 leading-relaxed">
								{callInterfaceDetails.description}
							</p>

							<Button
								type="primary"
								size="large"
								icon={<Mic className="w-5 h-5" />}
								onClick={startCallEvent}
								loading={isConnecting || isCheckingCredits}
								className={`h-16 w-64 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 border-0 shadow-2xl hover:shadow-xl transition-all duration-300 text-lg font-semibold${hasPermission === false ? ' px-8' : ''}`}
							>
								{hasPermission === false
									? 'Grant Microphone Permission'
									: isConnecting
										? 'Connecting...'
										: isCheckingCredits
											? 'Checking Compatibility...'
											: callInterfaceDetails.buttonText}
							</Button>

							<p className="text-sm text-gray-500 mt-4">{callInterfaceDetails.subText}</p>
						</>
					)}
				</div>
			</div>
		</>
	);
};

const TestimonialSection = () => (
	<>
		<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900" />
		<div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
		<div className="relative z-10 max-w-lg mx-auto p-6">
			<div className="mb-8 text-center">
				<div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-4">
					<TrophyFilled className="text-yellow-400 text-sm" />
					<span className="text-white text-xs font-medium">Trusted by 1,000+ businesses</span>
				</div>
				<h2 className="text-3xl font-bold text-white mb-3 leading-tight">
					<span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
						Double your revenue
					</span>{' '}
					with <br />
					<a
						href="https://www.myaifrontdesk.com"
						target="_blank"
						rel="noopener noreferrer"
						className="text-white text-4xl hover:text-blue-200 transition-colors duration-200 hover:scale-110 hover:underline flex justify-center items-center gap-1"
					>
						Frontdesk
						<ExternalLink className="w-3 h-3" />
					</a>
				</h2>
			</div>
			<div className="space-y-4">
				{testimonials.map((testimonial, index) => (
					<div
						key={testimonial.id}
						className="group bg-white/10 backdrop-blur-lg rounded-xl p-4 border border-white/20 hover:bg-white/15 hover:scale-105 transition-all duration-300 transform"
						style={{ animationDelay: `${index * 0.1}s` }}
					>
						<div className="flex mb-2">
							{[...Array(testimonial.rating || 5)].map((_, i) => (
								<StarFilled key={i} className="text-yellow-400 text-sm" />
							))}
						</div>
						<blockquote className="text-blue-50 text-xs leading-relaxed mb-3 italic">
							"{testimonial.content}"
						</blockquote>
						<div className="flex items-center gap-3">
							<div className="relative">
								<Avatar src={testimonial.avatar} size={36} className="border-2 border-white/30" />
							</div>
							<div className="flex flex-col">
								<h4 className="font-semibold text-white text-xs m-0">{testimonial.name}</h4>
								<p className="text-blue-200 text-xs m-0 flex items-center gap-1">
									<HeartFilled className="text-red-400 text-xs" />
									{testimonial.business}
								</p>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	</>
);

const ShareDemo: React.FC = () => {
	const { username } = useParams<{ username: string }>();
	const [searchParams] = useSearchParams();
	const clarity = useClarity();

	const roomRef = useRef<Room | null>(null);
	if (!roomRef.current) {
		roomRef.current = new Room();
	}
	const room = roomRef.current;

	const [isConnecting, setIsConnecting] = useState(false);
	const [roomConnectionState, setRoomConnectionState] = useState<ConnectionState>(
		ConnectionState.Disconnected,
	);

	const connectionStateRef = useRef({
		isConnecting: false,
		connectRequestSent: false,
		hasNotifiedAgent: false,
	});

	const callTimerInterval = useRef<NodeJS.Timeout | null>(null);
	const [hasPermission, setHasPermission] = useState<boolean | null>(null);

	const [showCaptchaModal, setShowCaptchaModal] = useState(false);
	const [postCaptchaAction, setPostCaptchaAction] = useState<(() => void) | null>(null);
	const [showCTA, setShowCTA] = useState(false);
	const [isCheckingCredits, setIsCheckingCredits] = useState(false);

	const source = searchParams.get('source') ?? 'share';

	const {
		data: clientDetails,
		isLoading: loadingClient,
		error: errorClient,
	} = useQuery<{ success: boolean; data: ClientDetails }>({
		queryKey: ['clientDetails', username],
		queryFn: () => getShareableClientDetails(username!),
		enabled: !!username,
	});

	const generateTokenMutation = useMutation({
		mutationFn: ({ username, testingNumber }: { username: string; testingNumber: string }) =>
			generateShareDemoToken(username, testingNumber),
		onSuccess: async (tokenData: TokenResponse) => {
			if (connectionStateRef.current.isConnecting || room.state === ConnectionState.Connected) {
				return;
			}

			try {
				connectionStateRef.current.isConnecting = true;

				if (room.state !== ConnectionState.Disconnected) {
					await room.disconnect();
				}

				await room.connect(SERVER_URL, tokenData.participantToken);
				await room.localParticipant.setMicrophoneEnabled(true);
			} catch {
				notification.error({
					message: 'Connection Failed',
					description: 'Failed to connect to the call. Please try again.',
				});
			} finally {
				connectionStateRef.current.isConnecting = false;
				connectionStateRef.current.connectRequestSent = false;
				setIsConnecting(false);
			}
		},
		onError: () => {
			notification.error({
				message: 'Authorization Failed',
				description: 'Failed to Authorize the call',
			});
			connectionStateRef.current.connectRequestSent = false;
			connectionStateRef.current.isConnecting = false;
			setIsConnecting(false);
		},
	});

	const checkCreditsMutation = useMutation({
		mutationFn: getShareableCredits,
		onSuccess: (data) => {
			if (!data.haveCredits) {
				notification.warning({
					message: 'Cannot Connect',
					description: 'Agent could not connect due to compatibility issues',
					duration: 2,
				});
				setIsConnecting(false);
				setIsCheckingCredits(false);
				return;
			}
			setIsConnecting(true);
			setIsCheckingCredits(false);
		},
		onError: () => {
			notification.error({
				message: 'Compatibility Check Failed',
				description: 'Failed to check compatibility. Please try again.',
			});
			setIsConnecting(false);
			setIsCheckingCredits(false);
		},
	});

	useEffect(() => {
		const RECAPTCHA_SITE_KEY = import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY as string;
		if (!document.querySelector('script[src*="recaptcha"]')) {
			const script = document.createElement('script');
			script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
			script.async = true;
			document.head.appendChild(script);
		}
	}, []);

	const verifyThenCall = (callback: () => void) => {
		setPostCaptchaAction(() => callback);
		setShowCaptchaModal(true);
	};

	const handleMakeCall = async () => {
		if (
			connectionStateRef.current.isConnecting ||
			connectionStateRef.current.connectRequestSent ||
			room.state === ConnectionState.Connected
		) {
			return;
		}

		if (!SERVER_URL) {
			notification.error({
				message: 'Configuration Error',
				description: 'Missing required LiveKit configuration.',
			});
			return;
		}

		if (!clientDetails?.data?.agentId) {
			notification.error({
				message: 'Error',
				description: 'Unable to make call. Please try again.',
			});
			return;
		}

		connectionStateRef.current.connectRequestSent = true;
		setIsCheckingCredits(true);

		try {
			const creditResult = await checkCreditsMutation.mutateAsync(username!);

			if (!creditResult.haveCredits) {
				connectionStateRef.current.connectRequestSent = false;
				return;
			}

			await generateTokenMutation.mutateAsync({
				username: clientDetails.data.agentId,
				testingNumber: clientDetails.data.testingNumber,
			});
		} catch {
			connectionStateRef.current.connectRequestSent = false;
			setIsConnecting(false);
			setIsCheckingCredits(false);
		}
	};

	useEffect(() => {
		const checkPermission = async () => {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
				stream.getTracks().forEach((track) => track.stop());
				setHasPermission(true);
			} catch {
				setHasPermission(false);
			}
		};
		checkPermission();
	}, []);

	const requestMicrophonePermission = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			stream.getTracks().forEach((track) => track.stop());
			setHasPermission(true);
			handleMakeCall();
		} catch {
			setHasPermission(false);
			notification.warning({
				message: 'Microphone Permission Required',
				description: 'Please enable microphone access in your browser settings to make test calls.',
				duration: 2,
			});
		}
	};

	const disconnectCall = useCallback(() => {
		if (room.state !== ConnectionState.Disconnected) {
			room.disconnect();
		}

		connectionStateRef.current.connectRequestSent = false;
		connectionStateRef.current.isConnecting = false;
		setRoomConnectionState(ConnectionState.Disconnected);
		setIsConnecting(false);

		if (callTimerInterval.current) {
			clearInterval(callTimerInterval.current);
			callTimerInterval.current = null;
		}
	}, [room]);

	useEffect(() => {
		const handleConnected = () => {
			setRoomConnectionState(ConnectionState.Connected);
			connectionStateRef.current.connectRequestSent = false;
			connectionStateRef.current.isConnecting = false;
			setIsConnecting(false);
		};

		const handleDisconnected = () => {
			connectionStateRef.current.connectRequestSent = false;
			connectionStateRef.current.isConnecting = false;
			setRoomConnectionState(ConnectionState.Disconnected);
			setIsConnecting(false);

			if (callTimerInterval.current) {
				clearInterval(callTimerInterval.current);
				callTimerInterval.current = null;
			}

			if (!userCancelledRef.current) {
				notification.info({
					message: 'Call Ended',
					description: 'Your test call has ended.',
					duration: 1,
				});
			}
			userCancelledRef.current = false;
			setShowCTA(true);
		};

		const handleConnectionStateChanged = (state: ConnectionState) => {
			setRoomConnectionState(state);
		};

		room.on(RoomEvent.Connected, handleConnected);
		room.on(RoomEvent.Disconnected, handleDisconnected);
		room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);

		return () => {
			room.off(RoomEvent.Connected, handleConnected);
			room.off(RoomEvent.Disconnected, handleDisconnected);
			room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
		};
	}, [room]);

	useEffect(() => {
		return () => {
			if (callTimerInterval.current) {
				clearInterval(callTimerInterval.current);
				callTimerInterval.current = null;
			}

			if (room.state === ConnectionState.Connected) {
				room.disconnect();
			}

			connectionStateRef.current.hasNotifiedAgent = false;
			connectionStateRef.current.connectRequestSent = false;
			connectionStateRef.current.isConnecting = false;
		};
	}, [room]);

	useFavicon({
		isUnbrandedPath: false,
	});

	useEffect(() => {
		if (clientDetails?.data?.businessName) {
			document.title = `${clientDetails.data.businessName} | Frontdesk | AI Receptionist`;
		} else {
			document.title = 'Frontdesk | AI Receptionist for Busy Businesses';
		}
	}, [clientDetails?.data?.businessName]);

	useEffect(() => {
		if (username && clientDetails?.data && source === 'email') {
			posthog.identify(username, {
				username,
				source,
				...clientDetails.data,
			});

			clarity('identify', username);

			clarity('set', {
				source,
				...clientDetails.data,
			});
		}
	}, [username, clientDetails]);

	if (loadingClient) {
		return <Spinner />;
	}

	if (errorClient && typeof errorClient === 'object' && (errorClient as any).code === 404) {
		return <Navigate to="/404" />;
	}
	if (errorClient) {
		return <ErrorBlock title="A technical error occurred. Please try again later." />;
	}
	if (!clientDetails?.data) {
		return <Navigate to="/404" />;
	}
	const { businessName } = clientDetails.data;

	const handleConsultationClick = () => {
		if (room.state === ConnectionState.Connected) {
			disconnectCall();
		}
		setShowCTA(true);
	};
	const captchaCompleted = (success: boolean) => {
		setShowCaptchaModal(false);
		if (success && postCaptchaAction) {
			postCaptchaAction();
		} else if (!success) {
			notification.error({
				message: 'Verification Failed',
				description: 'Captcha verification was not successful. Please try again.',
			});
		}
	};

	const getCTADetails = () => {
		if (source === 'email') {
			return {
				title: 'Ready to get started?',
				description: 'Check your email for a username and password to make more calls!',
				buttonText: 'Login',
				buttonLink: '/',
				buttonColor: 'from-blue-500 to-blue-600',
			};
		} else if (source === 'share') {
			return {
				title: 'Join thousands of businesses',
				description: 'Create your account and start using AI Frontdesk today.',
				buttonText: 'Get Started',
				buttonLink: '/register',
				buttonColor: 'from-green-500 to-green-600',
			};
		} else {
			return {
				title: 'Experience the future of customer service',
				description: 'Ready to transform your business with AI?',
				buttonText: 'Learn More',
				buttonLink: '/register',
				buttonColor: 'from-purple-500 to-purple-600',
			};
		}
	};

	const getCallInterfaceDetails = () => {
		if (source === 'email') {
			return {
				title: `${businessName} here's your free AI receptionist`,
				description:
					'Experience your own AI receptionist in real time. Give it a try and ask it anything you want!',
				buttonText: 'Start Free Call',
				subText: 'Free call • No commitment required',
			};
		} else {
			return {
				title: `Test the ${businessName} AI Receptionist`,
				description:
					"Experience how our AI handles calls in real-time. Connect directly to test the receptionist's capabilities.",
				buttonText: 'Start Test Call',
				subText: 'Test call • No commitment required',
			};
		}
	};

	const ctaDetails = getCTADetails();
	const callInterfaceDetails = getCallInterfaceDetails();

	const referToCTA = () => {
		if (window.gtag) {
			window.gtag('event', 'cta_pressed', {
				event_category: 'ShareDemo',
				event_label: source === 'email' ? 'Email Source' : 'Share Source',
			});
		}
		if (posthog) {
			posthog.capture('cta_pressed', {
				location: 'share_demo',
				source,
				username,
				...clientDetails?.data,
			});
		}
		clarity('event', 'cta_pressed');

		if (ctaDetails.buttonLink.startsWith('http')) {
			window.open(ctaDetails.buttonLink, '_blank', 'noopener,noreferrer');
		} else {
			window.location.href = ctaDetails.buttonLink;
		}
	};

	const handleTalkToSupport = () => {
		if (window.gtag) {
			window.gtag('event', 'talk_to_support_pressed', {
				event_category: 'ShareDemo',
				event_label: source === 'email' ? 'Email Source' : 'Share Source',
			});
		}
		if (posthog) {
			posthog.capture('talk_to_support_pressed', {
				location: 'share_demo',
				source,
				username,
				...clientDetails?.data,
			});
		}
		clarity('event', 'talk_to_support_pressed');
		window.open(SHARE_DEMO_CALENDLY_URL, '_blank', 'noopener,noreferrer');
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden font-sans">
			<div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
				<div className="bg-white rounded-xl p-2 shadow-lg border border-white/20 backdrop-blur-sm">
					<div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-200 flex items-center justify-center group transition-transform duration-200">
						<a
							href="https://www.myaifrontdesk.com"
							target="_blank"
							rel="noopener noreferrer"
							className="transition-transform duration-200 group-hover:scale-110"
						>
							<img
								src={logo}
								alt="AI Frontdesk Logo"
								className="w-10 h-10 rounded-lg transition-transform duration-200 group-hover:scale-110"
							/>
						</a>
					</div>
				</div>
			</div>

			{/* Top Right - Visit FrontDesk Link */}
			<div className="absolute top-4 right-4 z-20">
				<a
					href="https://www.myaifrontdesk.com"
					target="_blank"
					rel="noopener noreferrer"
					className="text-blue-700 hover:text-blue-900 transition-colors duration-200 text-sm font-medium flex items-center gap-1"
				>
					Visit FrontDesk
					<ExternalLink className="w-3 h-3" />
				</a>
			</div>

			<div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl" />
			<div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-500/20 rounded-full blur-3xl" />

			<div className="min-h-screen flex flex-col lg:flex-row w-full relative z-10">
				{/*Main Card */}
				<div className="lg:flex hidden lg:w-1/2 flex-col justify-center relative">
					<TestimonialSection />
				</div>

				<div className="w-full lg:w-1/2 flex items-center justify-center p-4">
					<div className="w-full max-w-md relative z-20 mt-28 lg:mt-0">
						<div className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden relative">
							<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500" />
							<div className="p-6">
								<AnimatePresence mode="wait">
									{!showCTA ? (
										<motion.div
											key="call-ui"
											initial={{ opacity: 0, x: 40 }}
											animate={{ opacity: 1, x: 0 }}
											exit={{ opacity: 0, x: -40 }}
											transition={{ duration: 0.3 }}
										>
											<RoomContext.Provider value={room}>
												<VoiceAssistantInterface
													businessName={businessName}
													isConnecting={isConnecting}
													isCheckingCredits={isCheckingCredits}
													onStartCall={
														hasPermission === false
															? requestMicrophonePermission
															: () => verifyThenCall(handleMakeCall)
													}
													onEndCall={disconnectCall}
													roomConnectionState={roomConnectionState}
													hasPermission={hasPermission}
													username={username}
													clientDetails={clientDetails}
													callInterfaceDetails={callInterfaceDetails}
													source={source}
												/>
											</RoomContext.Provider>
											<div
												className="mt-8 text-blue-600 text-base text-center cursor-pointer hover:underline transition-all duration-200"
												onClick={() => {
													handleConsultationClick();
												}}
											>
												{source === 'email' ? 'Start for Free' : 'Join Us'}
											</div>
										</motion.div>
									) : (
										<motion.div
											key="cta-ui"
											initial={{ opacity: 0, x: 40 }}
											animate={{ opacity: 1, x: 0 }}
											exit={{ opacity: 0, x: -40 }}
											transition={{ duration: 0.3 }}
										>
											<div className="flex items-center justify-between mb-4">
												<div
													className="text-sm text-gray-400 cursor-pointer hover:text-gray-600 transition text-left"
													onClick={() => setShowCTA(false)}
												>
													&larr; Test Call
												</div>
											</div>
											<div className="text-center space-y-6">
												<motion.div
													className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto"
													initial={{ scale: 0 }}
													animate={{ scale: 1 }}
													transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
												>
													<PhoneCall className="w-10 h-10 text-white" />
												</motion.div>

												<div className="space-y-3">
													<h2 className="text-2xl font-bold text-gray-900">{ctaDetails.title}</h2>
													<p className="text-gray-600 leading-relaxed">{ctaDetails.description}</p>
													{source === 'email' && (
														<div className="text-xs text-gray-500">
															Log in to your account to access your AI receptionist dashboard.
														</div>
													)}
												</div>

												<Button
													type="primary"
													size="large"
													className={`h-14 px-8 text-lg font-semibold bg-gradient-to-r ${ctaDetails.buttonColor} border-0 rounded-full shadow-lg hover:shadow-xl transition-all duration-300`}
													onClick={referToCTA}
												>
													{ctaDetails.buttonText}
												</Button>

												<div
													className="mt-8 text-blue-600 text-base text-center cursor-pointer hover:underline transition-all duration-200"
													onClick={handleTalkToSupport}
												>
													Talk to Support
												</div>
											</div>
										</motion.div>
									)}
								</AnimatePresence>
							</div>
						</div>
					</div>
				</div>

				{/* Mobile testimonials after call interface */}
				<div className="block lg:hidden w-full mt-24">
					<TestimonialSection />
				</div>
			</div>

			{showCaptchaModal && (
				<div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
					<motion.div
						initial={{ opacity: 0, scale: 0.9 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.9 }}
						className="w-full max-w-md border-2 bg-white/95 backdrop-blur-sm rounded-2xl overflow-hidden shadow-2xl"
					>
						<div className="p-8 space-y-6 text-center">
							<h2 className="text-2xl font-bold text-gray-900">Human Verification Required</h2>
							<p className="text-gray-600">
								Please verify you're human to test the AI receptionist
							</p>
							<ReCaptchaVerification
								trigger
								onVerificationComplete={(success) => captchaCompleted(success)}
							/>
						</div>
					</motion.div>
				</div>
			)}
		</div>
	);
};

const testimonials = [
	{
		id: 1,
		name: 'Sarah Johnson',
		business: "Johnson's Auto Repair",
		avatar:
			'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=64&h=64&fit=crop&crop=face',
		content:
			'Our AI receptionist has been a game-changer. We used to miss calls when we were under the hood, but now every customer gets immediate attention. Bookings are up 40%!',
		rating: 5,
	},
	{
		id: 2,
		name: 'Mike Chen',
		business: 'Smile Dental',
		avatar: '/mike-chen.png',
		content:
			"The appointment scheduling feature is incredible. Patients can book 24/7, and the AI handles rescheduling perfectly. It's like having a full-time receptionist at a fraction of the cost.",
		rating: 5,
	},
	{
		id: 3,
		name: 'Lisa Rodriguez',
		business: 'Bella Beauty Salon',
		avatar:
			'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=face',
		content:
			"I was skeptical at first, but clients love how professional and quick the responses are. The AI perfectly answers all my customer's questions and allows me to focus on running my salon!",
		rating: 5,
	},
];

export default ShareDemo;
